import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Bell, BellOff, CheckCheck } from "lucide-react";

const ICON = {
  booking_request: "🔔", booking_accepted: "✅", booking_denied: "❌",
  booking_cancelled: "🚫", booking_completed: "🎓", booking_rescheduled: "🔄",
};

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); }
    catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); window.dispatchEvent(new Event("notif-refresh")); };
  const markAll = async () => { await api.post("/notifications/read-all"); load(); window.dispatchEvent(new Event("notif-refresh")); };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between">
          <h1 className="font-heading font-extrabold text-3xl text-arctic-secondary tracking-tight" data-testid="notifications-title">Notifications</h1>
          {items.some((n) => !n.read) && (
            <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-read" className="rounded-full"><CheckCheck className="w-4 h-4 mr-1" /> Mark all read</Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-arctic-primary" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground"><BellOff className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No notifications yet.</p></div>
        ) : (
          <div className="space-y-3 mt-6" data-testid="notifications-list">
            {items.map((n) => (
              <Card key={n.notification_id} onClick={() => !n.read && markRead(n.notification_id)}
                data-testid={`notification-${n.notification_id}`}
                className={`p-4 rounded-2xl border cursor-pointer transition-colors duration-200 ${n.read ? "border-border bg-card" : "border-arctic-primary/30 bg-arctic-primary/5"}`}>
                <div className="flex items-start gap-3">
                  <span className="text-xl">{ICON[n.type] || "🔔"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-semibold text-arctic-secondary">{n.title}</span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-arctic-primary" />}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                    <span className="text-xs text-muted-foreground/70">{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
